import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '@/services/api/projects';
import type { CreateProjectRequest } from '@/types/project';

const PROJECTS_KEY = ['projects'] as const;
const projectDetailKey = (id: number) => ['project', id] as const;

export function useProjects(page = 0, size = 4) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, { page, size }],
    queryFn: async () => {
      const res = await projectsApi.list(page, size);
      return res.data.data;
    },
  });
}

export function useProject(id: number | undefined) {
  return useQuery({
    queryKey: projectDetailKey(id!),
    queryFn: async () => {
      const res = await projectsApi.getById(id!);
      return res.data.data;
    },
    enabled: !!id,
  });
}

export function usePublicProjects(page = 0, size = 100) {
  return useQuery({
    queryKey: ['public-projects', { page, size }],
    queryFn: async () => {
      const res = await projectsApi.listPublic(page, size);
      return res.data.data;
    },
  });
}

export function usePublicProject(id: number | undefined) {
  return useQuery({
    queryKey: ['public-project', id],
    queryFn: async () => {
      const res = await projectsApi.getPublicById(id!);
      return res.data.data;
    },
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateProjectRequest) => {
      const res = await projectsApi.create(data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
    },
  });
}

export function useUpdateProject(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateProjectRequest) => {
      const res = await projectsApi.update(id, data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
      queryClient.invalidateQueries({ queryKey: projectDetailKey(id) });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => projectsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
    },
  });
}
